import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet, Plus, Check, Search } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import SearchSelect from '../components/SearchSelect';
import { getAllPayments, searchPayments, createPayment, markPaymentPaid } from '../api/paymentsApi';
import { searchDrivers } from '../api/driversApi';
import { searchLoads } from '../api/loadsApi';
import { useToast } from '../context/ToastContext';

function Payments() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const driverIdFilter = searchParams.get('driver_id');

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [rateOverride, setRateOverride] = useState('');
  const [creating, setCreating] = useState(false);

  const hasActiveFilters = searchQuery || statusFilter || (dateFrom && dateTo);

  const fetchPayments = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      let result;
      if (hasActiveFilters) {
        result = await searchPayments({
          q: searchQuery,
          from: dateFrom,
          to: dateTo,
          status: statusFilter,
          page: pageNum,
          limit: 10,
        });
      } else {
        result = await getAllPayments(pageNum, 10);
      }

      if (result.success) {
        setPayments((prev) => (append ? [...prev, ...result.payments] : result.payments));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || t('common.genericError'));
        showToast('error', result.message || t('common.genericError'));
      }
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchPayments(1);
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, dateFrom, dateTo]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleSearchLoads = useCallback(async (query) => {
    return await searchLoads(query);
  }, []);

  const handleSearchDrivers = useCallback(async (query) => {
    return await searchDrivers(query);
  }, []);

  const handleCreatePayment = async (e) => {
    e.preventDefault();

    if (!selectedLoad || !selectedDriver) {
      showToast('error', 'Please select both a load and a driver.');
      return;
    }

    setCreating(true);
    try {
      const result = await createPayment({
        load_id: selectedLoad.id_load,
        driver_id: selectedDriver.id_driver,
        rate_percentage: rateOverride || null,
      });
      if (result.success) {
        showToast('success', `Payment created: ${result.amount_value}`);
        setSelectedLoad(null);
        setSelectedDriver(null);
        setRateOverride('');
        setShowCreateForm(false);
        fetchPayments(1);
      } else {
        showToast('error', result.message || 'Failed to create payment.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setCreating(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    const result = await markPaymentPaid(paymentId);
    if (result.success) {
      showToast('success', 'Payment marked as paid.');
      fetchPayments(1);
    } else {
      showToast('error', result.message || 'Failed to update payment.');
    }
  };

  const displayedPayments = driverIdFilter
    ? payments.filter((p) => String(p.driver_id) === driverIdFilter)
    : payments;

  const columns = [
    { accessorKey: 'driver_name', header: 'Driver' },
    {
      accessorKey: 'load_id',
      header: 'Load #',
      cell: (info) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => navigate(`/loads/${info.getValue()}`)}
        >
          #{info.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'load_price',
      header: 'Load Price',
      cell: (info) => `${Number(info.getValue()).toLocaleString()} ${info.row.original.currency}`,
    },
    {
      accessorKey: 'amount_value',
      header: 'Driver Pay',
      cell: (info) => `${Number(info.getValue()).toLocaleString()} ${info.row.original.currency}`,
    },
    {
      accessorKey: 'rate_percentage',
      header: 'Rate',
      cell: (info) => `${info.getValue()}%`,
    },
    {
      accessorKey: 'pay_status',
      header: 'Status',
      cell: (info) => {
        const isPaid = info.getValue() === 'paid';
        return (
          <span
            style={{
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              backgroundColor: isPaid ? '#d1fae5' : '#fef3c7',
              color: isPaid ? '#065f46' : '#92400e',
            }}
          >
            {isPaid ? 'Paid' : 'Not Paid Yet'}
          </span>
        );
      },
    },
    {
      accessorKey: 'payment_date',
      header: 'Payment Date',
      cell: (info) => info.getValue() || '—',
    },
    {
      id: 'view',
      header: '',
      cell: (info) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
          onClick={() => navigate(`/payments/${info.row.original.id_payment}`)}
        >
          View
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (info) =>
        info.row.original.pay_status !== 'paid' && (
          <button onClick={() => handleMarkPaid(info.row.original.id_payment)} style={styles.markPaidBtn}>
            <Check size={13} style={{ marginRight: 4 }} /> Mark Paid
          </button>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={t('nav.payments')}
          rightAction={
            <button onClick={() => setShowCreateForm(!showCreateForm)} style={styles.createBtn}>
              <Plus size={16} style={{ marginRight: 6 }} />
              Create Payment
            </button>
          }
        />

        <div style={{ padding: 24 }}>
          {driverIdFilter && (
            <div style={styles.filterBanner}>
              Showing payments for this driver only.{' '}
              <span
                onClick={() => navigate('/payments')}
                style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 700 }}
              >
                Clear filter
              </span>
            </div>
          )}

          <div style={styles.filtersRow}>
            <div style={styles.searchWrapper}>
              <Search size={16} style={styles.searchIcon} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by driver or load #"
                style={styles.searchInput}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="not paid yet">Not Paid Yet</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={styles.dateInput}
            />
            <span style={{ color: '#9ca3af', alignSelf: 'center' }}>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={styles.dateInput}
            />

            {hasActiveFilters && (
              <button onClick={handleResetFilters} style={styles.resetBtn}>
                Reset Filters
              </button>
            )}
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreatePayment} style={styles.createForm}>
              <SearchSelect
                label="Load"
                value={selectedLoad}
                onSelect={setSelectedLoad}
                fetchResults={handleSearchLoads}
                placeholder="Search by origin, destination, or load #"
                renderLabel={(load) => `#${load.id_load} — ${load.origin} → ${load.destination}`}
                renderOption={(load) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      #{load.id_load} — {load.origin} → {load.destination}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{load.load_status}</div>
                  </div>
                )}
              />

              <SearchSelect
                label="Driver"
                value={selectedDriver}
                onSelect={setSelectedDriver}
                fetchResults={handleSearchDrivers}
                placeholder="Search by name or email"
                renderLabel={(driver) => driver.driver_name}
                renderOption={(driver) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>{driver.driver_name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                      {driver.email} {driver.default_rate ? `• default ${driver.default_rate}%` : ''}
                    </div>
                  </div>
                )}
              />

              <label style={styles.label}>Rate Override % (optional — uses driver's default if blank)</label>
              <input
                type="number"
                step="0.01"
                value={rateOverride}
                onChange={(e) => setRateOverride(e.target.value)}
                style={styles.input}
              />

              <div style={styles.buttonRow}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setSelectedLoad(null);
                    setSelectedDriver(null);
                    setRateOverride('');
                  }}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button type="submit" disabled={creating} style={styles.submitBtn}>
                  {creating ? '...' : 'Create Payment'}
                </button>
              </div>
            </form>
          )}

          {loading && <LoadingSpinner fullScreen />}

          {!loading && error && (
            <EmptyState
              icon={Wallet}
              title={t('common.genericError')}
              message={error}
              onRetry={() => fetchPayments(1)}
              retryLabel={t('common.tryAgain')}
            />
          )}

          {!loading && !error && displayedPayments.length === 0 && (
            <EmptyState
              icon={Wallet}
              title="No payments found"
              message={hasActiveFilters ? 'Try different filters.' : 'Create your first payment record.'}
            />
          )}

          {!loading && !error && displayedPayments.length > 0 && (
            <>
              <DataTable columns={columns} data={displayedPayments} />
              {!driverIdFilter && hasMore && (
                <button
                  onClick={() => fetchPayments(page + 1, true)}
                  disabled={loadingMore}
                  style={styles.loadMoreBtn}
                >
                  {loadingMore ? t('common.loading') : t('common.loadMore')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  createBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  filterBanner: {
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  filtersRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchWrapper: {
    position: 'relative',
    minWidth: 240,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: 11,
    color: '#9ca3af',
  },
  searchInput: {
    width: '100%',
    padding: '9px 12px 9px 36px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  filterSelect: {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
  },
  dateInput: {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
  },
  resetBtn: {
    padding: '9px 14px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  createForm: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    maxWidth: 480,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 12,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  markPaidBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: 'none',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 12,
  },
};

export default Payments;